// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { formId } from "@conklin/Overrides/Widgets/ContactUs/ContactForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    physicalAddress: state.components.contactUsForm.fieldValues["physicalAddress"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PhysicalAddressStyles {
    physicalAddressTextField?: TextFieldPresentationProps;
}

export const physicalAddressStyles: PhysicalAddressStyles = {};

const styles = physicalAddressStyles;

const PhysicalAddress: React.FC<Props> = ({ fields, physicalAddress, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [physicalAddressErrorMessage, setPhysicalAddressErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.physicalAddress = validatePhysicalAddress;
        if (typeof physicalAddress !== "undefined") {
            validatePhysicalAddress();
        }
    }, [physicalAddress]);

    React.useEffect(() => {
        contactFormContext.validators.physicalAddress = validatePhysicalAddress;
    }, [isRequired]);

    const validatePhysicalAddress = () => {
        const errorMessage = isRequired && !physicalAddress ? (siteMessage("Field_Required", label) as string) : "";
        setPhysicalAddressErrorMessage(errorMessage);
        return !errorMessage;
    };

    const physicalAddressChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "physicalAddress", value: event.target.value });
    };

    return (
        <TextField
            {...styles.physicalAddressTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={physicalAddress || ""}
            error={physicalAddressErrorMessage}
            onChange={physicalAddressChangeHandler}
            type="text"
            {...{ formId }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PhysicalAddress),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Physical Address",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Physical Address",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
